import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FETCH_TEMPLATE_DATA } from "../redux/actions/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Document, Page } from "react-pdf";
import CustomButton from "../components/Button";
import CollabSignature from "../components/CollabSignature";
import SignatureModal from "../components/SignatureModal";
import {
  frontendAppUrl,
  serverBaseUrl,
} from "../config/envs";
import axios from "axios";
import { showErrorToast } from "../utils/toast";
import { PDFDocument, rgb } from "pdf-lib";
import { InputMask } from "primereact/inputmask";
import moment from "moment";
import CustomInput from "../components/Input";
import AlreadySigned from "./already_signed";
import { USA_STATE_LIST } from "../constant/constant";
import Loader from "../components/Loader";
import { buildSignSubmit } from "./operatorSignSubmit";
import { selectSignerFields, signerFlagsFor, signingOrderFor } from "./selectSignerFields";
import { legacyOperatorAddress } from "./operatorAddress";
import { lockedStampValues, pdfBuildErrorMessage } from "./pdfErrorMessage";
import CpFieldInput from "../components/CpFieldInput";
import {
  allTypedFieldsValid,
  cpBlockedReason,
  cpFieldContext,
  resolveCpFields,
  stateAt,
  stateNameOf,
  typedValue,
} from "./cpFieldRules";
import { cpBlockedText } from "./cpBlockedText";
import { stampCpFields } from "./cpStamp";
import { stampSignatureImage } from "./signatureStamp";
import { closeTabOrGoTo, pageAfterSubmit } from "./signTabClose";

const OperatorSign = ({ mode }) => {
  const dispatch = useDispatch();
  const { signSecretId } = useParams();
  const signerData = useSelector((state) => state?.signature?.signerData);
  const [pdfFile, setPdfFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [signImage, setSignImage] = useState(null);
  const [filledSignatureIds, setFilledSignatureIds] = useState([]);
  const [signBtnLoader, setSignBtnLoader] = useState(false);
  const [selectedFillId, setSelectedFillId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({});

  const currentPdfUrl = signerData?.data?.signLink?.created_s3_url;
  const operatorId = signerData?.data?.signLink?.operator_id;

  // Whose page this is: a loaded CP (6) or co-sign (7) link decides on any route, whatever the URL says.
  const { isCp, isCollabOperator } = signerFlagsFor(mode, signerData?.data?.signLink);
  const cpDetails = signerData?.data?.signLink?.collab_phys_details;

  const alreadySigned = signerData?.data?.signLink?.status === 2;

  // Narrow the template's flat field array to the current signer's fields: the
  // CP is signing_order 1, the co-signing operator 2, by route or by the link's
  // own type; the plain operator flow is unfiltered.
  const currentSigningOrder = signingOrderFor({ isCp, isCollabOperator });
  // The first signer (CP, order 1) absorbs any unordered legacy field so nothing
  // is dropped; the operator (order 2) stays strict.
  const scopedFields = selectSignerFields(
    signerData?.data?.signLink?.templateField,
    currentSigningOrder,
    { includeUnordered: currentSigningOrder === 1 }
  );

  useEffect(() => {
    const fetchPdf = async () => {
      if (currentPdfUrl) {
        try {
          const response = await fetch(currentPdfUrl);
          const arrayBuffer = await response.arrayBuffer();
          setPdfFile(arrayBuffer);
        } catch (error) {
          console.error("Error loading PDF:", error);
        }
      }
    };

    fetchPdf();
  }, [currentPdfUrl]);

  const getSignatureField = scopedFields?.filter(
    (field) => field?.field_type === "signature"
  );

  const getDateFields = scopedFields?.filter(
    (field) => field?.field_type === "date"
  );

  const getOperatorDetials = signerData?.data?.signLink?.operator_details;

  // Identity used for PDF stamping / readonly display / SignatureModal default name.
  // Operator branch is the exact, unchanged expression used everywhere below;
  // CP branch fills from the CP's own details (name is a single field, no address).
  const displayFullName = isCp
    ? cpDetails?.name
    : getOperatorDetials?.first_name + " " + getOperatorDetials?.last_name;

  const currentSignerFields = scopedFields?.filter(
    (field) =>
      field?.field_type !== "signature" &&
      field?.field_type !== "gender" &&
      field?.field_type !== "date" &&
      field?.field_type !== "phone"
  );

  // ENG-1826: the two CP pages fill fields from records and have the signer type the rest; /operator-sign keeps its own.
  const isCpPage = isCp || isCollabOperator;
  const signLink = signerData?.data?.signLink;
  const cpContext = cpFieldContext(signLink, { isCp }, moment().format("MM/DD/YYYY"), USA_STATE_LIST);
  // Plain values: scopedFields is a new array every render, so a memo here never held.
  const cpFields = isCpPage ? resolveCpFields(scopedFields || [], cpContext) : [];
  const cpTypedFieldsValid = !isCpPage || allTypedFieldsValid(cpFields, formValues);
  // ENG-1826: the field table holds an agreement it can't fill, so Submit stays off and the notice above it says why.
  const cpBlocked = isCpPage ? cpBlockedReason(cpFields, cpContext) : null;

  const handleOnSignatureClick = useCallback(
    ({ fillId }) => {
      if (filledSignatureIds.includes(fillId)) return;

      setSignBtnLoader(true);
      setSelectedFillId(fillId);
      if (signImage) {
        setTimeout(() => {
          setFilledSignatureIds((prev) => [...prev, fillId]);
          setSignBtnLoader(false);
        }, 500);
      } else {
        setIsOpen(true);
        setSignBtnLoader(false);
      }
    },
    [signImage, filledSignatureIds]
  );

  const signerIds = useMemo(() => {
    return getSignatureField
      ?.sort((a, b) => a.page - b.page)
      ?.map((element) => `signature_${element.id}`);
  }, [getSignatureField]);

  const removeSignature = async (file) => {
    if (isCp || isCollabOperator) {
      // CP and operator two-party signatures are transient (baked into the final
      // PDF, not persisted to operator_signature) — nothing to delete server-side.
      setSignImage(null);
      setFilledSignatureIds([]);
      return;
    }
    const response = await axios.delete(
      `${serverBaseUrl}/remove_operator_signature`,
      {
        data: {
          operatorId: operatorId,
        },
        headers: {
          "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
        },
      }
    );
    setSignImage(null);
    setFilledSignatureIds([]);
  };

  const handleOnSignatureChange = useCallback(
    ({ fillId }) => {
      setIsOpen(true);
      setSelectedFillId(fillId);
      setFilledSignatureIds((prev) => [...prev, fillId]);
    },
    [setIsOpen]
  );

  const getAdminSignature = useCallback(async () => {
    // Two-party operator co-sign is transient — don't fetch/persist a reusable
    // operator signature (its endpoints aren't part of this flow); draw fresh.
    if (operatorId && !isCollabOperator) {
      try {
        const response = await axios.get(
          `${serverBaseUrl}/get_operator_sign?operatorId=${operatorId}`,
          {
            headers: {
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );

        if (response?.data?.data[0]?.url) {
          setSignImage(response?.data?.data[0]?.url);

          if (signerData?.data?.signLink?.status === 1) {
            setFilledSignatureIds((prev) => [...prev]);
          }
        } else {
          setSignImage(null);
        }
      } catch (error) {
        setSignImage(null);
      }
    }
  }, [operatorId, isCollabOperator, signerData?.data?.signLink?.status]);

  const onSignatureSubmit = async (newImage, fillId) => {
    setSignImage(newImage);
    setFilledSignatureIds((prev) => [...prev, fillId]);
    await getAdminSignature();
    setIsLoading(false);
    setIsOpen(false);
  };

  useEffect(() => {
    getAdminSignature();
  }, [getAdminSignature, operatorId]);

  const uploadSignatureFile = useCallback(
    async (file, fillId) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );

        if (uploadResponse.data?.data?.url) {
          if (isCp || isCollabOperator) {
            // CP and operator two-party signatures aren't persisted operator-side —
            // set signImage straight from the freshly-uploaded file, skip
            // create_operator_sign/update_operator_sign entirely.
            onSignatureSubmit(uploadResponse.data?.data?.url, fillId);
            return;
          }
          if (signImage !== null) {
            const response = await axios.put(
              `${serverBaseUrl}/update_operator_sign`,
              {
                operatorId: operatorId,
                url: uploadResponse.data?.data?.url,
                path: uploadResponse.data?.data?.url.split("/").pop(),
              },
              {
                headers: {
                  "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
                },
              }
            );
            if (response?.data?.success) {
              onSignatureSubmit(uploadResponse.data?.data?.url, fillId);
            } else {
              showErrorToast(
                "Something went wrong while uploading signature, please try again"
              );
              setIsLoading(false);
            }
            return;
          }
          const response = await axios.post(
            `${serverBaseUrl}/create_operator_sign`,
            {
              operatorId: operatorId,
              url: uploadResponse.data?.data?.url,
              path: uploadResponse.data?.data?.url.split("/").pop(),
            },
            {
              headers: {
                "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
              },
            }
          );
          if (response?.data?.success) {
            onSignatureSubmit(uploadResponse.data?.data?.url, fillId);
          } else {
            showErrorToast(
              "Something went wrong while uploading signature, please try again"
            );
            setIsLoading(false);
          }
        }
      } catch (err) {
        setIsLoading(false);
      }
    },
    [isCp, isCollabOperator, operatorId, signerData?.data?.signLink?.sign_secret_id, signImage]
  );

  const uploadGeneratedPdf = useCallback(
    async (file) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload-sign-template-document`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );
        if (uploadResponse.data?.data?.url) {
          const { url, body, isCp: submittedAsCp } = buildSignSubmit({
            serverBaseUrl,
            mode,
            signLink: signerData?.data?.signLink,
            finalDocumentUrl: uploadResponse.data?.data?.url,
            dateCreated: moment().format("YYYY-MM-DD"),
            dateSigned: moment().format("YYYY-MM-DD:HH:mm:ss"),
          });
          const response = await axios.post(url, body, {
            headers: {
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          });

          if (response?.data?.success) {
            // ENG-2015: a tab the portal opened closes itself; any other goes where it always did
            closeTabOrGoTo(window, pageAfterSubmit(submittedAsCp, frontendAppUrl));
          } else {
            showErrorToast(
              submittedAsCp
                ? "Failed to submit collaborating physician sign link"
                : "Failed to submit operator sign link"
            );
            setIsLoading(false);
          }
        }
      } catch (err) {
        showErrorToast("Failed to submit the signed document. Please try again.");
        setIsLoading(false);
      }
    },
    [mode, signerData?.data?.signLink]
  );

  const generateModifiedPdf = async () => {
    if (!pdfFile) return false;

    try {
      const pdfArrayBuffer = pdfFile;
      const existingPdf = await PDFDocument.load(pdfArrayBuffer, {
        ignoreEncryption: true,
      });
      const pages = existingPdf.getPages();

      if (signImage) {
        await stampSignatureImage(existingPdf, signImage, getSignatureField);

        if (isCpPage) {
          await stampCpFields(existingPdf, cpFields, formValues);
        } else {
          getDateFields.forEach(
            ({ height, width, page_number, position_x, position_y }, i) => {
              const pageIndex = page_number - 1;
              if (pageIndex >= 0 && pageIndex < pages.length) {
                const page = pages[pageIndex];
                const { height: pageHeight } = page.getSize();

                const textValue = moment(moment()).format("MM/DD/YYYY") || "";
                if (textValue) {
                  page.drawText(textValue, {
                    x: position_x + 20,
                    y:
                      i === 2
                        ? pageHeight - position_y - 31
                        : i === 1
                        ? pageHeight - position_y - 24
                        : pageHeight - position_y - height,
                    size: 12,
                    color: rgb(0, 0, 0),
                    maxWidth: width,
                  });
                }
              }
            }
          );

          currentSignerFields.forEach(
            (
              { width, height, page_number, position_x, position_y, field_name },
              i
            ) => {
              const pageIndex = page_number - 1;
              if (pageIndex >= 0 && pageIndex < pages.length) {
                const page = pages[pageIndex];
                const { height: pageHeight } = page.getSize();

                const name =
                  field_name === "Name" ||
                  field_name === "Full Name" ||
                  field_name === "full name" ||
                  field_name === "name"
                    ? displayFullName
                    : "";

                // CP details carry no address (only name/phone/email) — operator
                // address stamping is unchanged.
                const address = isCp
                  ? ""
                  : field_name === "address" || field_name === "Address"
                    ? legacyOperatorAddress(getOperatorDetials, USA_STATE_LIST)
                    : "";

                if (name) {
                  page.drawText(name, {
                    x: position_x + 20,
                    y:
                      i === 0
                        ? pageHeight - position_y - height
                        : pageHeight - position_y - 25,
                    size: 12,
                    color: rgb(0, 0, 0),
                    maxWidth: width,
                  });
                }
                if (address) {
                  page.drawText(address, {
                    x: position_x + 20,
                    y: pageHeight - position_y - 34,
                    size: 12,
                    color: rgb(0, 0, 0),
                    maxWidth: width,
                  });
                }
              }
            }
          );
        }
      }

      const modifiedPdf = await existingPdf.save();
      const blob = new Blob([modifiedPdf], { type: "application/pdf" });
      await uploadGeneratedPdf(blob);
    } catch (error) {
      console.error("Error generating modified PDF:", error);
      const lockedValues = lockedStampValues({
        isCpPage,
        cpFields,
        recordValues: [displayFullName, legacyOperatorAddress(getOperatorDetials, USA_STATE_LIST)],
      });
      showErrorToast(pdfBuildErrorMessage(error, lockedValues));
      return false;
    }
  };

  const handleInputChange = useCallback((fieldName, value, fieldType) => {
    setFormValues((prevValues) => {
      return { ...prevValues, [fieldName]: value };
    });
  }, []);

  useEffect(() => {
    dispatch({
      type: FETCH_TEMPLATE_DATA,
      payload: signSecretId,
    });
  }, [signSecretId, dispatch]);

  if (alreadySigned) {
    return <AlreadySigned />;
  }

  return (
    <Layout>
      <div className="relative flex flex-col w-[600px] gap-4 overflow-x-hidden mx-auto items-center justify-start min-h-screen px-4 sm:px-6 lg:px-8 overflow-y-auto bg-gray-50">
        {Array.from(new Array(numPages), (_, index) => (
          <div key={`page_container_${index + 1}`} className="relative">
            <Document
              file={{ data: pdfFile }}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages);
                setPdfLoaded(true);
              }}
              loading={
                <div className="flex items-center justify-center h-full">
                  <Loader className="w-screen relative z-50 bg-white" />
                </div>
              }
            >
              <Page
                pageNumber={index + 1}
                width={600}
                className="mb-4"
                loading={
                  <div className="flex items-center justify-center h-full">
                    <Loader className="w-screen relative z-50 bg-white" />
                  </div>
                }
                error={
                  <div className="flex items-center justify-center h-full">
                    <Loader className="w-screen relative z-50 bg-white" />
                  </div>
                }
              />
            </Document>

            {pdfLoaded && (
              <>
                {!isCpPage && currentSignerFields
                  ?.filter((field) => field.page_number === index + 1)
                  ?.map((field, i) => {
                    const uniqueKey = `${field?.field_name}_${index}_${i}`;
                    const uniqueName = uniqueKey;

                    return (
                      <div
                        key={uniqueKey}
                        className="absolute pointer-events-auto"
                        style={{
                          left: field.position_x,
                          top: field.position_y,
                          width:
                            field?.field_name === "address" ||
                            field?.field_name === "Address"
                              ? "100%"
                              : field.width,
                        }}
                      >
                        <CustomInput
                          name={uniqueName}
                          type={field?.field_type}
                          disabled
                          placeholder={field?.field_name}
                          className="border-none text-xs disabled:bg-transparent disabled:cursor-default"
                          style={{
                            height: field.height,
                          }}
                          value={
                            field?.field_type === "date"
                              ? moment(
                                  formValues[uniqueName],
                                  "MM/DD/YYYY"
                                ).format("YYYY-MM-DD")
                              : field?.field_name === "Name" ||
                                field?.field_name === "Full Name" ||
                                field?.field_name === "full name" ||
                                field?.field_name === "name"
                              ? displayFullName
                              : !isCp &&
                                (field?.field_name === "address" ||
                                  field?.field_name === "Address")
                              ? getOperatorDetials?.address_line_1 +
                                ", " +
                                getOperatorDetials?.city +
                                ", " +
                                stateAt(getOperatorDetials?.state, USA_STATE_LIST) +
                                ", " +
                                getOperatorDetials?.zip_code
                              : formValues[uniqueName] || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              uniqueName,
                              e.target.value,
                              field?.field_type
                            )
                          }
                        />
                      </div>
                    );
                  })}

                {isCpPage &&
                  cpFields
                    .filter(({ field }) => field.page_number === index + 1)
                    .map((entry) => (
                      <div
                        key={entry.key}
                        className="absolute pointer-events-auto"
                        style={{
                          left: entry.field.position_x,
                          top: entry.field.position_y,
                          width: entry.field.width,
                          zIndex: 10,
                        }}
                      >
                        <CpFieldInput
                          name={entry.key}
                          field={entry.field}
                          input={entry.input}
                          locked={entry.locked}
                          value={entry.locked ? entry.value : typedValue(entry, formValues)}
                          onChange={(next) => handleInputChange(entry.key, next, entry.field.field_type)}
                        />
                      </div>
                    ))}

                {getSignatureField
                  ?.filter((field) => field.page_number === index + 1)
                  ?.map((field, i) => (
                    <div
                      key={`signature_${index}_${i}`}
                      className="absolute pointer-events-auto"
                      style={{
                        left: field.position_x,
                        top: field.position_y,
                        zIndex: 50,
                      }}
                    >
                      {!signImage ||
                      !filledSignatureIds?.includes(
                        `signature_${index}_${i}`
                      ) ? (
                        <CustomButton
                          className="pointer-events-auto"
                          onClick={() => {
                            handleOnSignatureClick({
                              fillId: `signature_${index}_${i}`,
                            });
                          }}
                          label="Sign"
                          labelColor="black"
                          id={`signature_${index}_${i}`}
                          isLoading={signBtnLoader}
                        />
                      ) : null}
                      {signImage &&
                      filledSignatureIds?.includes(
                        `signature_${index}_${i}`
                      ) ? (
                        <div className="max-w-52 max-h-12">
                          <CollabSignature
                            className="!max-h-12"
                            showBorder={false}
                            handleOnSignatureClick={() =>
                              handleOnSignatureClick({
                                fillId: `signature_${index}_${i}`,
                              })
                            }
                            loading={isLoading}
                            removeSignatures={removeSignature}
                            onChange={() =>
                              handleOnSignatureChange({
                                fillId: `signature_${index}_${i}`,
                              })
                            }
                            allButtonIds={[...signerIds, "submit"]}
                            userSignauter={signImage}
                            id={`signature_${index}_${i}`}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}

                {!isCpPage && getDateFields
                  ?.filter((field) => field.page_number === index + 1)
                  ?.map((date, i) => {
                    const uniqueKey = `date_${index}_${i}`;
                    const uniqueName = uniqueKey;
                    return (
                      <div
                        key={uniqueKey}
                        className="absolute pointer-events-auto border-none border-gray-300 flex items-center"
                        style={{
                          left: date.position_x,
                          top: date.position_y,
                          width: date.width,
                          zIndex: 10,
                        }}
                      >
                        <InputMask
                          className="pointer-events-auto text-xs border-none focus:outline-none focus:border-none focus:ring-0"
                          style={{
                            height: date.height,
                            width: date.width,
                          }}
                          name={uniqueName}
                          id={uniqueKey}
                          placeholder={"MM/DD/YYYY"}
                          value={moment(moment()).format("MM/DD/YYYY")}
                          mask={"99/99/9999"}
                          slotChar="MM/DD/YYYY"
                          disabled
                          inputMode="numeric"
                          onChange={(e) =>
                            handleInputChange(
                              uniqueName,
                              e.target.value,
                              date.field_type
                            )
                          }
                        />
                      </div>
                    );
                  })}
              </>
            )}
          </div>
        ))}

        {cpBlocked && (
          <p data-testid="cp-license-blocked" className="text-sm text-center text-red-700">
            {cpBlockedText(cpBlocked, stateNameOf(signLink?.state, USA_STATE_LIST))}
          </p>
        )}
        <CustomButton
          label="Submit"
          labelColor="black"
          isLoading={isLoading}
          className="mb-4"
          onClick={generateModifiedPdf}
          disabled={
            !signImage ||
            isLoading ||
            filledSignatureIds?.length !== getSignatureField?.length ||
            !cpTypedFieldsValid ||
            Boolean(cpBlocked)
          }
        />
      </div>
      <SignatureModal
        isLoading={isLoading}
        operatorSign={true}
        defaultName={displayFullName}
        signatureType={""}
        onSubmit={(signedDoc) => {
          uploadSignatureFile(signedDoc, selectedFillId);
        }}
        onClose={() => setIsOpen(false)}
        open={isOpen}
      />
    </Layout>
  );
};

export default OperatorSign;
